import { useState, useEffect, type ReactNode } from "react";
import {
  FileText,
  Loader2,
  Edit,
  Copy,
  Trash2,
  Eye,
  Share2,
  Pencil,
  WifiOff,
  CheckCircle2,
  BookmarkPlus,
} from "lucide-react";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { ItineraryStatus } from "../builder/models/itinerary.model";
import { useI18n } from '@/hooks/useI18n';
import { useModal } from "@/contexts/ModalContext";
import { toast } from "@/hooks/useToast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { OfflineItinerariesService } from "@/services/offline-itineraries.service";

/** Handler e contesto con cui un itinerario espone le sue azioni (menu o pulsanti). */
export interface ItineraryActionHandlers {
  onEdit?: (itineraryId: string) => void;
  onDelete?: (itineraryId: string) => void;
  onDuplicate?: (itineraryId: string) => void;
  onViewTravelRequest?: (travelRequestId: string) => void;
  onViewItinerary?: (itineraryId: string) => void;
  onUpdateStatus?: (itineraryId: string, status: ItineraryStatus) => void;
  /** Chiamata alla chiusura della modal di condivisione dopo un invio riuscito:
   *  lo status dell'itinerario è cambiato, la lista in memoria va rifetchata. */
  onShared?: () => void;
  isAdmin?: boolean;
  isTraveler?: boolean;
  userRole?: string;
}

export interface ItineraryActionItem {
  id: string;
  label: string;
  icon: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  destructive?: boolean;
  /**
   * L'azione mostra il proprio avanzamento (PDF, download offline): chi la ospita in una
   * modale non deve chiuderla, altrimenti l'avanzamento sparisce.
   */
  keepsContext?: boolean;
}

/**
 * Se aprendo l'itinerario lo si modifica o lo si guarda soltanto. Il cliente guarda
 * sempre; chi ci lavora modifica, tranne i viaggi conclusi (data di fine passata) e le
 * proposte scadute, dove non c'e' piu' niente da cambiare. Decide solo l'etichetta: il
 * builder apre comunque, e cosa vi si puo' fare lo stabilisce resolveBuilderCapabilities.
 */
export const canEditItinerary = (
  itinerary: DashboardItinerary,
  { isTraveler, userRole }: Pick<ItineraryActionHandlers, 'isTraveler' | 'userRole'>,
): boolean =>
  !isTraveler &&
  userRole !== 'traveler' &&
  itinerary.status !== 'expired' &&
  itinerary.detailedStatus !== 'past';

interface Options extends ItineraryActionHandlers {
  itinerary: DashboardItinerary;
  onPdfStateChange?: (isGenerating: boolean, isSuccess: boolean) => void;
}

/**
 * Le azioni di un itinerario, in gruppi. Unica definizione per il menu a tre puntini
 * (tabella e card) e per i pulsanti dell'anteprima: regole di ruolo e di stato vivono
 * solo qui, quindi i due punti non possono divergere.
 */
export function useItineraryActionItems({
  itinerary,
  onDelete,
  onDuplicate,
  onViewTravelRequest,
  onViewItinerary,
  onShared,
  isAdmin,
  isTraveler,
  userRole,
  onPdfStateChange,
}: Options): ItineraryActionItem[][] {
  const { t } = useI18n();
  const { openModal } = useModal();
  const { userData, logoUrl, logoSmallUrl } = useAuth();
  const [isGeneratingPDFBE, setIsGeneratingPDFBE] = useState(false);
  const [isDownloadingOffline, setIsDownloadingOffline] = useState(false);
  const [isAlreadyDownloaded, setIsAlreadyDownloaded] = useState(false);

  useEffect(() => {
    OfflineItinerariesService.isDownloaded(itinerary.id).then(setIsAlreadyDownloaded);
  }, [itinerary.id]);

  const handleDownloadOffline = async () => {
    if (isDownloadingOffline) return;
    setIsDownloadingOffline(true);
    try {
      // Le attività passano dalla RPC: il pacchetto offline finisce su IndexedDB del
      // dispositivo del cliente, quindi i costi per travel object non devono nemmeno
      // arrivare al browser. La RPC li azzera server-side per il traveler.
      const [{ data: itineraryRecord }, { data: activities }] = await Promise.all([
        supabase.from('itineraries').select('*').eq('id', itinerary.id).single(),
        supabase.rpc('get_itinerary_activities' as any, { p_itinerary_id: itinerary.id }),
      ]);

      if (!itineraryRecord) throw new Error('Itinerary not found');

      const dayMap = new Map<string, any[]>();
      for (const act of activities ?? []) {
        if (!dayMap.has(act.date)) dayMap.set(act.date, []);
        dayMap.get(act.date)!.push(act);
      }
      const itinerary_days = Array.from(dayMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, acts]) => ({ date, activities: acts }));

      const fullItinerary = { ...itineraryRecord, itinerary_days };

      const sharedBy = {
        first_name: userData?.first_name ?? '',
        last_name: userData?.last_name ?? '',
        logo_url: logoUrl ?? undefined,
        agency_name: userData?.agency_name ?? undefined,
      };

      await OfflineItinerariesService.downloadItinerary(fullItinerary, sharedBy);
      setIsAlreadyDownloaded(true);

      toast({ variant: 'success',
        title: t('offline.downloadSuccess'),
        description: t('offline.downloadSuccessDesc'),
      });
    } catch (err) {
      console.error('Error downloading offline:', err);
      toast({
        title: t('errors.generic'),
        description: t('offline.downloadError'),
        variant: 'destructive',
      });
    } finally {
      setIsDownloadingOffline(false);
    }
  };

  const handleGeneratePDFBE = async () => {
    if (isGeneratingPDFBE) return;
    setIsGeneratingPDFBE(true);
    onPdfStateChange?.(true, false);
    try {
      // Logo già risolto da AuthContext secondo la preferenza dell'utente
      const { data, error } = await supabase.functions.invoke("generate-pdf", {
        body: JSON.stringify({
          itineraryId: itinerary.id,
          ...(logoUrl ? { logoUrl } : {}),
          ...(logoSmallUrl ? { logoSmallUrl } : {}),
        }),
      });
      if (error || !data?.pdfBase64)
        throw new Error(error?.message || "No PDF returned");
      const byteChars = atob(data.pdfBase64);
      const byteArr = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++)
        byteArr[i] = byteChars.charCodeAt(i);
      const blob = new Blob([byteArr], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.fileName || `itinerary-${itinerary.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      onPdfStateChange?.(false, true);
      setTimeout(() => {
        onPdfStateChange?.(false, false);
      }, 3500);

      toast({ variant: 'success',
        title: t("itinerary.pdfGenerated"),
        description: t("itinerary.pdfGeneratedDesc"),
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      onPdfStateChange?.(false, false);
      toast({
        title: t("errors.generic"),
        description: t("itinerary.pdfGenerationError"),
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPDFBE(false);
    }
  };

  // Stessi ruoli che possono aprire il Playground (e che la RPC accetta: per gli
  // altri solleva eccezione). I traveler non vedono la voce in nessun caso.
  const canSaveAsTemplate =
    !isTraveler &&
    (isAdmin ||
      ["admin", "travel_designer", "travel_advisor_member", "agency"].includes(userRole ?? ""));

  const groups: ItineraryActionItem[][] = [];

  // 1. Apertura: proposta, richiesta, backoffice
  if (isTraveler || userRole === "agency") {
    if (userRole === "agency") {
      groups.push([{
        id: 'view-request',
        label: String(t("travelRequest.viewTripRequest")),
        icon: <Eye className="h-4 w-4" />,
        onSelect: () => onViewTravelRequest?.(itinerary.travelRequest.id),
      }]);
    }
  } else {
    groups.push([
      canEditItinerary(itinerary, { isTraveler, userRole })
        ? {
            id: 'open',
            label: String(t("itinerary.editItinerary")),
            icon: <Pencil className="h-4 w-4" />,
            onSelect: () => onViewItinerary?.(itinerary.id),
          }
        : {
            id: 'open',
            label: String(t("itinerary.viewItineraryProposal")),
            icon: <Eye className="h-4 w-4" />,
            onSelect: () => onViewItinerary?.(itinerary.id),
          },
      {
        id: 'view-request',
        label: String(t("travelRequest.viewTripRequest")),
        icon: <Eye className="h-4 w-4" />,
        onSelect: () => onViewTravelRequest?.(itinerary.travelRequest.id),
      },
      ...(isAdmin
        ? [{
            id: 'backoffice',
            label: String(t("itinerary.openInBackoffice")),
            icon: <Edit className="h-4 w-4" />,
            onSelect: () =>
              window.open(`https://www.backoffice.place2be.it/itineraries/edit/${itinerary.id}`, "_blank"),
            keepsContext: true,
          }]
        : []),
    ]);
  }

  // 2. Invio al cliente
  if (!isTraveler) {
    groups.push([{
      id: 'send',
      label: String(t("itinerary.sendToClient")),
      icon: <Share2 className="h-4 w-4" />,
      onSelect: () => openModal("send-quote", { itinerary, onSuccessClose: onShared }),
    }]);
  }

  // 3. Documenti: PDF e copia offline (quest'ultima solo da app installata o in locale)
  const documents: ItineraryActionItem[] = [{
    id: 'pdf',
    label: String(isGeneratingPDFBE ? t("itinerary.generatingPDF") : t("itinerary.generatePDF")),
    icon: isGeneratingPDFBE
      ? <Loader2 className="h-4 w-4 animate-spin" />
      : <FileText className="h-4 w-4" />,
    onSelect: handleGeneratePDFBE,
    disabled: isGeneratingPDFBE,
    keepsContext: true,
  }];
  if (window.matchMedia('(display-mode: standalone)').matches || window.location.hostname === 'localhost') {
    documents.push({
      id: 'offline',
      label: String(isDownloadingOffline
        ? t("offline.downloading")
        : isAlreadyDownloaded
          ? t("offline.downloadedAgain")
          : t("offline.downloadOffline")),
      icon: isDownloadingOffline
        ? <Loader2 className="h-4 w-4 animate-spin" />
        : isAlreadyDownloaded
          ? <CheckCircle2 className="h-4 w-4 text-green-600" />
          : <WifiOff className="h-4 w-4" />,
      onSelect: handleDownloadOffline,
      disabled: isDownloadingOffline,
      keepsContext: true,
    });
  }
  groups.push(documents);

  // 4. Template: un template si ricava da un viaggio in qualsiasi stato
  if (canSaveAsTemplate) {
    groups.push([{
      id: 'template',
      label: String(t("itinerary.saveAsTemplate")),
      icon: <BookmarkPlus className="h-4 w-4" />,
      onSelect: () => openModal("save-as-template", { itinerary }),
    }]);
  }

  // 5. Duplica / elimina: non sugli itinerari inviati o conclusi
  if (
    !isTraveler &&
    userRole !== "agency" &&
    itinerary.status !== "sended" &&
    itinerary.status !== "completed"
  ) {
    groups.push([
      {
        id: 'duplicate',
        label: String(t("common.duplicate")),
        icon: <Copy className="h-4 w-4" />,
        onSelect: () => onDuplicate?.(itinerary.id),
      },
      {
        id: 'delete',
        label: String(t("common.delete")),
        icon: <Trash2 className="h-4 w-4" />,
        onSelect: () => onDelete?.(itinerary.id),
        destructive: true,
      },
    ]);
  }

  return groups;
}
